import { Box, Chip, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useElementHeight } from "../../../hooks/useElementHeight";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { ChangesSource, type DayStats, type StatsCalculationParams } from "../../../utils/models";
import { calculateDayStats } from "../../../utils/statsCalculations";
import { formatCurrency, formatNumber, getChipColor } from "../../../utils/utilFunctions";
import { CustomCalendar } from "../../CustomCalendar/CustomCalendar";
import { CensusDisplay } from "../../staffingBlock/CensusDisplay";

type MobileStaffingHeaderProps = {
	date: DateTime;
	setDate: (date: DateTime) => void;
	weekIndex: number;
	onRootHeightChange?: (height: number) => void;
};

export const MobileStaffingHeader: FC<MobileStaffingHeaderProps> = ({ date, setDate, weekIndex, onRootHeightChange }) => {
	const { hospitalData, chosenDepartment, timezone, departmentNurseTypes, federalHolidaysUSASet, transformedShifts, isHospital, wageData } = useHospitalData();
	const canViewWage = useRevealWage();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const rootRef = useRef<HTMLDivElement>(null);
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();

	// `weekIndex` is derived in MobileStaffing from the URL date, which is never validated against the
	// loaded weeks — so it can be -1 (deep link to an out-of-window date) or point at a week that is
	// still hydrating. Descendants that index `weeks[weekIndex]` without a guard then throw on
	// `undefined`; CensusDisplay -> EditCensusMenu did exactly that and took the whole page down.
	const hasLoadedWeek = !!hospitalData?.weeks?.[weekIndex]?.weeklyShifts?.[0]?.days?.length;
	// A populated `weeks` array is what separates "this date isn't in the schedule" from "the schedule
	// hasn't arrived yet", so the snackbar stays silent during normal startup and refreshes.
	const isDateOutOfRange = weekIndex === -1 && !!hospitalData?.weeks?.length;

	const height = useElementHeight(rootRef);
	useEffect(() => {
		if (height > 0) onRootHeightChange?.(height);
	}, [height, onRootHeightChange]);

	// Deps mirror ShiftStats, which builds the same params from the same captures. `weekIndex` and the
	// whole `hospitalData`/`chosenDepartment` were previously omitted or narrowed to one field, so this
	// could keep serving a previous day's PPD/HPPD/cost after the data underneath it changed.
	const dayStats = useMemo<DayStats | undefined>(() => {
		if (!hospitalData?.weeks?.[weekIndex]?.weeklyShifts || isHospital) return;

		const params: StatsCalculationParams = {
			weekIndex: weekIndex,
			weeklyShifts: hospitalData.weeks[weekIndex].weeklyShifts,
			nurses: hospitalData.nurses,
			dayIndex: date.weekday % 7,
			department: chosenDepartment,
			timezone: timezone,
			rolesToDisplay: departmentNurseTypes,
			hospitalData: hospitalData,
			federalHolidaysUSASet,
			jobManagementEnabled,
			wageData,
		};

		return calculateDayStats(params, !isHospital, timezone);
	}, [hospitalData, weekIndex, date, chosenDepartment, departmentNurseTypes, timezone, federalHolidaysUSASet, isHospital, jobManagementEnabled, wageData]);

	const chipColor = useMemo(() => getChipColor(dayStats, chosenDepartment), [dayStats, chosenDepartment]);

	useEffect(() => {
		if (isDateOutOfRange) showSnackbar(t("staffing.date.out.of.range"), "error");
	}, [isDateOutOfRange, showSnackbar, t]);

	// Bail after every hook has run so hook order stays stable across the loaded/unloaded transition.
	if (!hasLoadedWeek) return null;

	return (
		<Box
			ref={rootRef}
			sx={{
				position: "sticky",
				top: 0,
				width: "100%",
				zIndex: 5,
				bgcolor: "white",
				boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
			}}
		>
			<CustomCalendar shifts={transformedShifts} value={date} onChange={setDate} showOneWeek={true} showTitle={true} />
			<Box>
				<Divider />

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-evenly",
						height: "42px",
						alignItems: "center",
					}}
				>
					<Chip
						label={<span style={{ color: chipColor.color }}>{dayStats ? formatNumber((dayStats.totalHours / dayStats.wantedHours) * 100, 1) : 0}%</span>}
						sx={{
							fontSize: "0.75rem",
							bgcolor: chipColor.backgroundColor,
							height: "20px",
							"& .MuiChip-label": {
								padding: "0 8px",
								lineHeight: "20px",
								color: chipColor.color,
							},
							mb: 0.5,
							gap: "10px",
						}}
					/>

					<CensusDisplay source={ChangesSource.Staffing} dayDate={date} displayTitle censusValue={dayStats?.census ?? 0} containerSx={{ gap: 1.2 }} />
				</Box>

				<Divider />

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-evenly",
						height: "42px",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}>Total Costs:</Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							{!canViewWage ? "$0.0" : dayStats && formatCurrency(dayStats.totalSpent)}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}> PPD: </Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							${!canViewWage ? "$0.0" : dayStats?.ppd}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}> HPPD: </Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							{dayStats?.hppd}{" "}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
